import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useMobileBack } from '@/contexts/MobileBackContext'
import { cn } from '@/lib/utils'

import { Error404 } from '../ErrorState/Error404'
import { LottieSpinner } from '../LottieSpinner/LottieSpinner'

import { WeScreenHeader } from './WeScreenHeader'

export type WeScreenProps = {
  children?: React.ReactNode
  useHeader?: boolean
  title?: string
  useHeaderBackButton?: boolean
  onClickBack?: () => void
  useHomeIcon?: boolean
  hideHeaderOnMobile?: boolean
  useNextButton?: boolean
  onClickNextButton?: (key: any) => void
  isLoadingNextButton?: boolean
  isDisabledNextButton?: boolean
  useSkipButton?: boolean
  onClickSkipButton?: (key: any) => void
  rightSideHeaderContent?: React.ReactNode
  isLoading?: boolean
  isError?: boolean
  contentClassName?: string
}

export const WeScreen = ({
  children,
  useHeader = true,
  title,
  useHeaderBackButton = true,
  onClickBack,
  useHomeIcon = false,
  hideHeaderOnMobile = false,
  useNextButton = true,
  onClickNextButton,
  isLoadingNextButton,
  isDisabledNextButton,
  useSkipButton = false,
  onClickSkipButton,
  rightSideHeaderContent,
  isLoading = false,
  isError = false,
  contentClassName,
}: WeScreenProps) => {
  const { t } = useTranslation()
  const screenId = useLocation().key
  const { setBack } = useMobileBack()

  useEffect(() => {
    if (hideHeaderOnMobile && onClickBack) {
      setBack(onClickBack, { useHomeIcon })
      return () => setBack(null)
    }
    return undefined
  }, [hideHeaderOnMobile, onClickBack, useHomeIcon, setBack])

  const header = useHeader && (
    <div className={cn(hideHeaderOnMobile && 'max-md:hidden')}>
      <WeScreenHeader
        key={`header-${screenId}`}
        title={title}
        useBackButton={useHeaderBackButton}
        onClickBack={onClickBack}
        useHomeIcon={useHomeIcon}
        useContinueButton={useNextButton}
        onClickNext={onClickNextButton}
        isLoading={isLoadingNextButton}
        disabled={isDisabledNextButton || isLoading}
        useSkipButton={useSkipButton}
        onClickSkipButton={onClickSkipButton}
        customButton={rightSideHeaderContent}
      />
    </div>
  )

  const contentClasses = cn(
    'flex flex-col flex-1 min-h-0 w-full p-4 max-md:p-2',
    contentClassName,
  )

  if (isError) {
    return (
      <div className="flex flex-col w-full h-full" key={`screen-${screenId}`}>
        {header}
        <div className="flex justify-center items-center w-full flex-1 min-h-0">
          <Error404 absolute={false} />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full" key={`screen-${screenId}`}>
        {header}
        <div className="flex justify-center items-center w-full flex-1 min-h-0">
          <LottieSpinner
            width={100}
            height={100}
            text={t('feedback.common.loading')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full h-full" key={`screen-${screenId}`}>
      {header}
      <div className={contentClasses} key={`content-${screenId}`}>
        {children}
      </div>
    </div>
  )
}
